import React, { useState } from 'react';
import styled from 'styled-components';

interface MobileSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  theme: any;
  asset?: string;
  onSubmissionSuccess?: () => void;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
  
  /* Enhanced mobile support */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  
  /* Prevent zoom on iOS */
  touch-action: manipulation;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid ${props => props.theme.border};
  
  /* Enhanced mobile scrolling */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  
  /* Prevent content jumping */
  box-sizing: border-box;
  
  /* Better shadow for mobile */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  padding: 20px 20px 16px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${props => props.theme.text};
  cursor: pointer;
  padding: 0;
  line-height: 1;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const SubmissionOption = styled.div`
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.theme.background};

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.hoverBackground};
  }
`;

const OptionIcon = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
`;

const OptionTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const OptionDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${props => props.theme.subText};
  line-height: 1.4;
`;

const LinkSubmissionForm = styled.div`
  margin-top: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 16px; /* Prevent zoom on iOS */
  background: ${props => props.theme.inputBackground || props.theme.surface};
  color: ${props => props.theme.text};
  box-sizing: border-box;
  
  /* Enhanced mobile support */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none; /* Remove iOS styling */
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.subText};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 16px; /* Prevent zoom on iOS */
  background: ${props => props.theme.inputBackground || props.theme.surface};
  color: ${props => props.theme.text};
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;
  
  /* Enhanced mobile support */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.subText};
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  /* Enhanced mobile touch */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  min-height: 48px; /* Accessibility minimum */
  
  &:hover {
    opacity: 0.9;
  }
  
  &:active {
    transform: scale(0.98);
    background: ${props => props.theme.primary + 'dd'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled.button`
  width: 100%;
  padding: 12px;
  background: transparent;
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 12px;
  
  &:hover {
    background: ${props => props.theme.hoverBackground};
  }
`;

const SuccessMessage = styled.div`
  background: rgba(46, 204, 113, 0.2);
  border: 2px solid rgba(46, 204, 113, 0.5);
  border-radius: 12px;
  padding: 24px 20px;
  margin-bottom: 16px;
  color: ${props => props.theme.text};
  text-align: center;
  
  /* Make it very prominent */
  box-shadow: 0 4px 16px rgba(46, 204, 113, 0.3);
  animation: successSlideIn 0.4s ease-out;
  
  @keyframes successSlideIn {
    from {
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  div:first-child {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: rgba(39, 174, 96, 1);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.15);
  border: 1px solid rgba(231, 76, 60, 0.4);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  color: ${props => props.theme.text};
  text-align: center;
  
  /* Make it more prominent */
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.2);
`;

const MessageButton = styled.button`
  background: linear-gradient(135deg, #2ECC71 0%, #27AE60 100%);
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 25px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  box-shadow: 0 3px 12px rgba(46, 204, 113, 0.4);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 18px rgba(46, 204, 113, 0.5);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(46, 204, 113, 0.3);
  }
`;

type SubmissionMode = 'select' | 'link' | 'manual';

const MobileSubmissionModal: React.FC<MobileSubmissionModalProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  theme,
  asset = 'btc',
  onSubmissionSuccess
}) => {
  const [mode, setMode] = useState<SubmissionMode>('select');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkContactInfo, setLinkContactInfo] = useState({
    email: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [formData, setFormData] = useState({
    expert_name: '',
    twitter_handle: '',
    prediction: '',
    target_date: '',
    submitter_email: ''
  });

  const resetForm = () => {
    setMode('select');
    setLinkUrl('');
    setLinkContactInfo({ email: '', name: '' });
    setFormData({
      expert_name: '',
      twitter_handle: '',
      prediction: '',
      target_date: '',
      submitter_email: ''
    });
    setMessage(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSuccessClose = () => {
    onSubmissionSuccess?.(); // Call success callback when user dismisses success message
    handleClose();
  };

  const submitLink = async () => {
    if (!linkUrl.trim()) {
      setMessage({ type: 'error', text: 'Please enter a valid URL' });
      return;
    }

    // Basic URL validation
    if (!linkUrl.includes('twitter.com') && !linkUrl.includes('x.com')) {
      setMessage({ type: 'error', text: 'Please enter a valid Twitter/X.com URL' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/submissions/quick-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          linkUrl: linkUrl,
          type: 'link',
          userEmail: linkContactInfo.email || null,
          userName: linkContactInfo.name || null,
          submitted_via: 'mobile'
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ 
          type: 'success', 
          text: result.message || 'Link submitted successfully!'
        });
        setLinkUrl('');
        setLinkContactInfo({ email: '', name: '' });
        // Don't call onSubmissionSuccess immediately - let user see message first
        // onSubmissionSuccess?.();
        // Don't auto-close - let user read the message and close manually
      } else {
        setMessage({ type: 'error', text: 'Failed to submit. Please try again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    }

    setLoading(false);
  };

  const submitManual = async () => {
    if (!formData.expert_name || !formData.prediction || !formData.target_date) {
      setMessage({ type: 'error', text: 'Please fill in the expert name, prediction, and target date' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/submissions/quick-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          expertName: formData.expert_name,
          twitterHandle: formData.twitter_handle,
          predictionType: 'target', // Default to target for mobile submissions
          price: formData.prediction,
          targetDate: formData.target_date,
          notes: formData.submitter_email, // Use email field for notes for now
          asset: asset || 'btc',
          submitted_via: 'mobile'
        })
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: 'Prediction submitted successfully! We\'ll review it and add it to the chart.' 
        });
        // Don't call onSubmissionSuccess immediately - let user see message first
        // onSubmissionSuccess?.();
        // Don't auto-close - let user read the message and close manually
      } else {
        setMessage({ type: 'error', text: 'Failed to submit. Please try again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    }

    setLoading(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()} theme={theme}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>Submit {asset?.toUpperCase() || 'BTC'} Prediction</ModalTitle>
          <CloseButton onClick={handleClose} theme={theme}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {message && message.type === 'success' ? (
            <SuccessMessage theme={theme}>
              <div>✅ {message.text}</div>
              <MessageButton theme={theme} onClick={handleSuccessClose}>
                Got it!
              </MessageButton>
            </SuccessMessage>
          ) : (
            <>
              {message && message.type === 'error' && (
                <ErrorMessage theme={theme}>
                  <div>❌ {message.text}</div>
                </ErrorMessage>
              )}

              {mode === 'select' && (
            <>
              <SubmissionOption 
                onClick={() => setMode('link')} 
                theme={theme}
              >
                <OptionIcon>🔗</OptionIcon>
                <OptionTitle theme={theme}>Submit Link</OptionTitle>
                <OptionDescription theme={theme}>
                  Paste a Twitter/X.com link with a Bitcoin prediction
                </OptionDescription>
              </SubmissionOption>

              <SubmissionOption 
                onClick={() => setMode('manual')} 
                theme={theme}
              >
                <OptionIcon>✍️</OptionIcon>
                <OptionTitle theme={theme}>Manual Entry</OptionTitle>
                <OptionDescription theme={theme}>
                  Enter prediction details manually
                </OptionDescription>
              </SubmissionOption>
            </>
          )}

          {mode === 'link' && (
            <LinkSubmissionForm>
              <BackButton onClick={() => setMode('select')} theme={theme}>
                ← Back to options
              </BackButton>
              
              <FormGroup>
                <Label theme={theme}>Twitter/X.com URL *</Label>
                <Input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://twitter.com/username/status/..."
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Your Email (optional)</Label>
                <Input
                  type="email"
                  value={linkContactInfo.email}
                  onChange={(e) => setLinkContactInfo({...linkContactInfo, email: e.target.value})}
                  placeholder="your@email.com (we'll notify you when processed)"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Your Name (optional)</Label>
                <Input
                  type="text"
                  value={linkContactInfo.name}
                  onChange={(e) => setLinkContactInfo({...linkContactInfo, name: e.target.value})}
                  placeholder="Your name"
                  theme={theme}
                />
              </FormGroup>

              <Button
                onClick={submitLink}
                disabled={loading}
                theme={theme}
              >
                {loading ? 'Submitting...' : 'Submit Link'}
              </Button>
            </LinkSubmissionForm>
          )}

          {mode === 'manual' && (
            <LinkSubmissionForm>
              <BackButton onClick={() => setMode('select')} theme={theme}>
                ← Back to options
              </BackButton>
              
              <FormGroup>
                <Label theme={theme}>Expert Name *</Label>
                <Input
                  type="text"
                  value={formData.expert_name}
                  onChange={(e) => handleInputChange('expert_name', e.target.value)}
                  placeholder="e.g. Michael Saylor"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Twitter Handle</Label>
                <Input
                  type="text"
                  value={formData.twitter_handle}
                  onChange={(e) => handleInputChange('twitter_handle', e.target.value)}
                  placeholder="e.g. @saylor"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Prediction *</Label>
                <TextArea
                  value={formData.prediction}
                  onChange={(e) => handleInputChange('prediction', e.target.value)}
                  placeholder="e.g. Bitcoin will reach $150,000 by end of 2024"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Target Date</Label>
                <Input
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => handleInputChange('target_date', e.target.value)}
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>Your Email (optional)</Label>
                <Input
                  type="email"
                  value={formData.submitter_email}
                  onChange={(e) => handleInputChange('submitter_email', e.target.value)}
                  placeholder="For updates on your submission"
                  theme={theme}
                />
              </FormGroup>

              <Button
                onClick={submitManual}
                disabled={loading}
                theme={theme}
              >
                {loading ? 'Submitting...' : 'Submit Prediction'}
              </Button>
            </LinkSubmissionForm>
          )}
          
          </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MobileSubmissionModal;